import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { resolveRequestErrorCode } from '$/api/utils/resolveRequestErrorMessage';
import { getMyIpDetail } from '$/domains/myIp/myIpService';
import { subscribeMyIpChanged } from '$/domains/myIp/changeEvents';
import type { MyIpItem } from '$/domains/myIp/types';

interface AssetDetailState {
  contextKey: string;
  status: 'idle' | 'loading' | 'success' | 'error' | 'forbidden' | 'unavailable';
  item?: MyIpItem;
  error?: unknown;
}

/** 列表只提供选中身份；右栏不将列表投影当作已鉴权的完整设定。 */
function emptyState(contextKey: string, enabled: boolean): AssetDetailState {
  return { contextKey, status: enabled ? 'loading' : 'idle' };
}

/** 识别详情接口约定的无权/不可访问状态，未知业务码仍交给通用错误态重试。 */
function errorStatus(error: unknown): AssetDetailState['status'] {
  const value = error as { status?: number; response?: { status?: number; }; } | null;
  const status = value?.status ?? value?.response?.status;
  if (resolveRequestErrorCode(error) === '15602') return 'unavailable';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'unavailable';
  return 'error';
}

/** 资产库只读取选中的完整详情；账号、分类或选择变化立即清空，忽略取消后迟到的响应。 */
export function useMyIpAssetDetail(scopeKey: string, enabled: boolean, assetType: 'personal' | 'official', ipId?: string) {
  const active = enabled && Boolean(ipId);
  const contextKey = JSON.stringify([scopeKey, active, assetType, ipId]);
  const currentKey = useRef(contextKey);
  currentKey.current = contextKey;
  const controller = useRef<AbortController>();
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<AssetDetailState>(() => emptyState(contextKey, active));

  /** 重试和 IP 更新通知都重新鉴权；请求期间不继续展示可能已撤回的资源。 */
  const refresh = useCallback(() => {
    if (currentKey.current !== contextKey || !active) return;
    controller.current?.abort();
    setState(emptyState(contextKey, active));
    setRevision((value) => value + 1);
  }, [contextKey, active]);

  /** 更新通知不保存角色数据，各挂载实例按自身当前身份重新读取。 */
  useEffect(() => subscribeMyIpChanged(refresh), [refresh]);

  /** 请求只依赖稳定的选中身份，不因列表对象或语言更新而重复读取。 */
  useEffect(() => {
    const abort = new AbortController();
    controller.current = abort;
    setState(emptyState(contextKey, active));
    /** 即使传输层不响应 abort，也禁止旧 IP 或账号的成功和失败覆盖当前右栏。 */
    const isCurrent = () => !abort.signal.aborted && currentKey.current === contextKey;
    /** 官方列表和个人列表使用同一鉴权接口，返回类型必须仍匹配当前资产分类。 */
    const load = async () => {
      if (!active || !ipId) return;
      try {
        const item = await getMyIpDetail(ipId, abort.signal);
        if (!isCurrent()) return;
        if (item.ipId !== ipId || item.assetType !== assetType || item.status !== 'complete') {
          setState({ contextKey, status: 'unavailable' });
          return;
        }
        setState({ contextKey, status: 'success', item });
      } catch (error) {
        if (isCurrent()) setState({ contextKey, status: errorStatus(error), error });
      }
    };
    load();
    return () => abort.abort();
  }, [contextKey, active, assetType, ipId, revision]);

  const visible = state.contextKey === contextKey ? state : emptyState(contextKey, active);
  return { ...visible, refresh };
}
